import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { useTalentMapAnalytics } from '@/hooks/usePerformance';
import { CategoryCurve } from '@/components/performance/talent-map/CategoryCurve';
import { curvePointMeta, type CurvePoint } from '@/components/performance/talent-map/talentMapShared';
import { AreaProgressChart } from '@/components/performance/AreaProgressChart';
import { buildTalentMapStats, RECENT_TOP_MONTHS, type TalentAnalyticsRow, type TalentTopWatch } from '../../../../../shared/talent-map-stats';
import { toDMY } from '../../../../../shared/dates';

/**
 * Tops a mirar de cerca (Euge 24/09): lista de Top Performers confirmados que
 * ingresaron o fueron promovidos dentro de la ventana. `total` = Tops del
 * universo filtrado, para que el "3 de 12" tenga contexto.
 */
function TopWatchCard({ title, subtitle, items, total, dateLabel }: {
  title: string;
  subtitle: string;
  items: TalentTopWatch[];
  total: number;
  dateLabel: (date: string) => string;
}) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-baseline justify-between gap-3 mb-1">
          <h3 className="font-semibold">{title}</h3>
          <span className="text-2xl font-bold tabular-nums text-emerald-600">{items.length}</span>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          {subtitle} · {t('performance.talent_map.analytics.watch_count', { count: items.length, total })}
        </p>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('performance.talent_map.analytics.watch_empty')}</p>
        ) : (
          <ul className="divide-y">
            {items.map(p => (
              <li key={p.email} className="py-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-sm">
                <span className="font-medium">{p.name} {p.lastName}</span>
                <span className="text-xs text-muted-foreground">
                  {[p.area, p.leader?.includes('@') ? p.leader.split('@')[0] : p.leader].filter(Boolean).join(' · ')}
                </span>
                <span className="w-full text-xs text-muted-foreground sm:w-auto">{dateLabel(p.date)}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * "Analytics people" (doc 02/09) — visual agregada de COMPAÑÍA para People:
 * filtros (Clapper, Líder, Impact Level, Área, País), KPIs siempre en % sobre
 * el universo filtrado, curva general e insights automáticos. Todo se
 * recalcula en memoria con shared/talent-map-stats (patrón dtalk-stats).
 */
export function TalentMapAnalyticsTab() {
  const { t } = useTranslation();
  const { data, isLoading } = useTalentMapAnalytics();

  const [fClapper, setFClapper] = useState('');
  const [fLeader, setFLeader] = useState('');
  const [fLevel, setFLevel] = useState('');
  const [fArea, setFArea] = useState('');
  const [fCountry, setFCountry] = useState('');

  const rows: TalentAnalyticsRow[] = (data ?? []) as TalentAnalyticsRow[];

  const unique = (fn: (r: TalentAnalyticsRow) => string | null) =>
    Array.from(new Set(rows.map(fn).filter(Boolean) as string[])).sort((a, b) => a.localeCompare(b, 'es'));
  const uniqueLeaders = useMemo(() => unique(r => r.leader), [rows]);
  const uniqueLevels = useMemo(() => unique(r => r.impactLevel), [rows]);
  const uniqueAreas = useMemo(() => unique(r => r.area), [rows]);
  const uniqueCountries = useMemo(() => unique(r => r.country), [rows]);
  const uniqueClappers = useMemo(
    () => Array.from(new Map(rows.map(r => [r.email, `${r.name} ${r.lastName}`.trim()])).entries())
      .sort((a, b) => a[1].localeCompare(b[1], 'es')),
    [rows],
  );

  const filtered = useMemo(() => rows.filter(r => {
    if (fClapper && r.email !== fClapper) return false;
    if (fLeader && (r.leader ?? '') !== fLeader) return false;
    if (fLevel && (r.impactLevel ?? '') !== fLevel) return false;
    if (fArea && (r.area ?? '') !== fArea) return false;
    if (fCountry && (r.country ?? '') !== fCountry) return false;
    return true;
  }), [rows, fClapper, fLeader, fLevel, fArea, fCountry]);

  const stats = useMemo(() => buildTalentMapStats(filtered), [filtered]);
  const hasFilters = !!(fClapper || fLeader || fLevel || fArea || fCountry);
  const clear = () => { setFClapper(''); setFLeader(''); setFLevel(''); setFArea(''); setFCountry(''); };

  const points: CurvePoint[] = stats.curve.map(c => ({
    key: c.key,
    count: c.count,
    pct: c.pct,
    ...curvePointMeta(c.key, t as (key: string, fallback?: string) => string),
  }));

  // El % es sobre quienes APLICAN (sin onboarding ni licencia); el subtítulo
  // dice cuántos quedan afuera para que "87 de 310" no parezca un error contra
  // la nómina.
  const excluded = [
    stats.onboarding > 0 ? t('performance.talent_map.analytics.excluded_onb', { count: stats.onboarding }) : null,
    stats.licencia > 0 ? t('performance.talent_map.analytics.excluded_lic', { count: stats.licencia }) : null,
  ].filter((x): x is string => !!x);
  const applicableSub = (n: number) =>
    excluded.length > 0
      ? t('performance.talent_map.analytics.of_applicable_excl', { n, m: stats.applicable, excl: excluded.join(' · ') })
      : t('performance.talent_map.analytics.of_total', { n, m: stats.applicable });

  const kpis = [
    { key: 'completed', pct: stats.completedPct, sub: applicableSub(stats.completed), label: t('performance.talent_map.analytics.kpi_completed'), accent: 'text-emerald-600' },
    { key: 'uncompleted', pct: stats.uncompletedPct, sub: applicableSub(stats.uncompleted), label: t('performance.talent_map.analytics.kpi_uncompleted'), accent: 'text-gray-500' },
    { key: 'top', pct: stats.topPct, sub: t('performance.talent_map.analytics.clappers_count', { count: stats.top }), label: t('performance.talent_map.analytics.kpi_top'), accent: 'text-emerald-600' },
    { key: 'low', pct: stats.lowPct, sub: t('performance.talent_map.analytics.clappers_count', { count: stats.low }), label: t('performance.talent_map.analytics.kpi_low'), accent: 'text-red-600' },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <WinclapLoader size={48} className="text-foreground/25" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t('performance.talent_map.analytics.title', 'Analytics people')}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t('performance.talent_map.analytics.subtitle', 'Visual agregada de compañía — todos los indicadores se recalculan según los filtros.')}
        </p>
      </div>

      {/* Filtros (doc §4) */}
      <div className="flex flex-wrap items-center gap-2">
        {[
          { value: fClapper, onChange: setFClapper, all: t('performance.talent_map.team_tab.filter_all_clappers', 'Clapper: todos'), opts: uniqueClappers },
          { value: fLeader, onChange: setFLeader, all: t('performance.talent_map.filter_all_managers'), opts: uniqueLeaders.map(l => [l, l.includes('@') ? l.split('@')[0] : l] as [string, string]) },
          { value: fLevel, onChange: setFLevel, all: t('performance.talent_map.filter_all_levels'), opts: uniqueLevels.map(l => [l, l] as [string, string]) },
          { value: fArea, onChange: setFArea, all: t('performance.talent_map.team_tab.filter_all_areas'), opts: uniqueAreas.map(a => [a, a] as [string, string]) },
          { value: fCountry, onChange: setFCountry, all: t('performance.talent_map.filter_all_countries'), opts: uniqueCountries.map(c => [c, c] as [string, string]) },
        ].map((sel, i) => (
          <select
            key={i}
            value={sel.value}
            onChange={e => sel.onChange(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground max-w-[200px]"
          >
            <option value="">{sel.all}</option>
            {sel.opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        ))}
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clear} className="gap-1 h-9 text-xs text-muted-foreground">
            <X className="h-3 w-3" />
            {t('performance.talent_map.clear_filters')}
          </Button>
        )}
      </div>

      {filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            {t('performance.talent_map.no_results')}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* KPIs (doc §5) — % siempre sobre el universo filtrado */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {kpis.map(k => (
              <Card key={k.key}>
                <CardContent className="p-4 text-center">
                  <p className={`text-3xl font-bold tabular-nums ${k.accent}`}>{k.pct}%</p>
                  <p className="text-xs font-medium mt-1">{k.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{k.sub}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Curva general (doc §6) */}
          <Card>
            <CardContent className="p-5">
              <h3 className="font-semibold mb-1">{t('performance.talent_map.analytics.curve_title', 'Distribución de la compañía')}</h3>
              <p className="text-xs text-muted-foreground mb-2">
                {t('performance.talent_map.analytics.curve_universe', { count: stats.total })}
              </p>
              <CategoryCurve points={points} height={260} showAxis />
              <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3">
                {points.map(p => (
                  <div key={p.key} className="flex items-center gap-2 text-sm">
                    <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
                    <span className={p.count > 0 ? 'text-foreground/90' : 'text-muted-foreground'}>
                      {p.label} — {p.count} ({p.pct}%)
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Avance por área (pedido Sol 14/09: para compartir en el canal) */}
          {stats.byArea.length > 0 && (
            <Card>
              <CardContent className="p-5">
                <h3 className="font-semibold mb-1">{t('performance.talent_map.analytics.area_progress_title')}</h3>
                <p className="text-xs text-muted-foreground mb-3">
                  {t('performance.talent_map.analytics.area_progress_subtitle', {
                    done: stats.completed, total: stats.applicable, pct: stats.completedPct,
                  })}
                </p>
                <AreaProgressChart
                  rows={stats.byArea.map(a => ({ ...a, done: a.completed, total: a.applicable }))}
                  tooltip={a => [
                    `${a.completed} / ${a.applicable} (${a.pct}%)${a.onboarding > 0
                      ? ` · ${t('performance.talent_map.analytics.area_tooltip_onb', { count: a.onboarding })}`
                      : ''}`,
                    t('performance.talent_map.analytics.kpi_completed'),
                  ]}
                />
              </CardContent>
            </Card>
          )}

          {/* Tops a mirar de cerca (Euge 24/09): ingresos < 6 meses y promovidos hace < 6 meses */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TopWatchCard
              title={t('performance.talent_map.analytics.top_recent_hires_title', { months: RECENT_TOP_MONTHS })}
              subtitle={t('performance.talent_map.analytics.top_recent_hires_sub', { since: toDMY(stats.recentSince) })}
              items={stats.topRecentHires}
              total={stats.top}
              dateLabel={d => t('performance.talent_map.analytics.joined_on', { date: toDMY(d) })}
            />
            <TopWatchCard
              title={t('performance.talent_map.analytics.top_promoted_title')}
              subtitle={t('performance.talent_map.analytics.top_promoted_sub', { since: toDMY(stats.recentSince), months: RECENT_TOP_MONTHS })}
              items={stats.topRecentlyPromoted}
              total={stats.top}
              dateLabel={d => t('performance.talent_map.analytics.promoted_on', { date: toDMY(d) })}
            />
          </div>

          {/* Insights clave (doc §7) */}
          {stats.insights.length > 0 && (
            <div>
              <h3 className="font-semibold mb-2">{t('performance.talent_map.analytics.insights_title', 'Insights clave')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {stats.insights.map(ins => (
                  <Card key={ins.id} className="bg-muted/50 border-0 shadow-none">
                    <CardContent className="p-3">
                      <p className="text-sm text-foreground/90">
                        {t(`performance.talent_map.analytics.insight_${ins.id}`, {
                          group: ins.group, count: ins.count, pct: ins.pct,
                        })}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
